import React, { useEffect, useState, useCallback } from 'react';
import {
  Building2, Users, CreditCard, Shield, Copy, Check,
  Save, CheckCircle, Crown, ChevronDown, UserPlus, Mail,
  Upload, ImageIcon, X, Clock, Trash2, Briefcase, History, LogIn, LogOut, KeyRound, Eye,
  UsersRound, Hash, Archive, RotateCcw, Search, Database,
} from 'lucide-react';
import { useRoute } from '../lib/router';
import {
  Card, CardHeader, CardBody, Button, Input, Label, Select, Badge, Alert, Modal,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { usePlan } from '../contexts/PlanContext';
import { useAuth } from '../contexts/AuthContext';
import { Can, AccessDenied } from '../contexts/PermissionsContext';
import { useNotifications } from '../contexts/PushNotificationsContext';
import { useEncryption } from '../contexts/EncryptionContext';
import { updateFirm, uploadFirmLogo, listFirmUsers, inviteTeamMember, resendInvite, addStaffRecord, removeUnconfirmedUser, deleteTeamMember, deleteEmployee, listFirmRoles, setUserRole, listEmployees, listAccessLog, listArchivedChannels, archiveFirmChannel, firmMessagingExport, setFirmMessageRetention, previewMessageRetention, verifyFirmIntegrity, recordMessagingSeals, listLatestSeals, sendSealDigest, getFirmDlpSettings, updateFirmDlpSettings, listRecentDlpEvents, listSealAnchors, fetchSealAnchorProof, triggerSealAnchor, upgradeAnchors, closeMyFirm, getMyFirmSeatInfo, getMyFirmStorageInfo } from '../lib/db';
import { DLP_RULE_LABELS } from '../components/messages/dlpScanner';
import { fullName, formatDate } from '../lib/formatters';
import UpgradeModal from '../components/UpgradeModal';
import StorageUsageCard from '../components/StorageUsageCard';
import SampleDataSection from '../components/settings/SampleDataSection';
import toast from '../lib/toast';

// ── Ghana regions ──────────────────────────────────────────────────────────────
const GHANA_REGIONS = [
  'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central',
  'Eastern', 'Greater Accra', 'North East', 'Northern',
  'Oti', 'Savannah', 'Upper East', 'Upper West', 'Volta',
  'Western', 'Western North',
];

// Invitable roles — must match the system roles seeded by migration 031
// (Admin, Partner, Attorney, Associate, Paralegal, Clerk, IT).
// Ordered loosely by scope of access for the dropdown UX:
//   Admin / Partner — full firm access (Partner = no role mgmt)
//   Attorney / Associate / Paralegal / Clerk — case access tiers
//   IT             — settings + team mgmt + documents, NO case/billing
//   Of Counsel     — legacy alias kept for firms that already used it
//                    (it isn't a seeded firm_role but the column accepts free text)
const ROLES = ['Admin', 'Partner', 'Attorney', 'Associate', 'Paralegal', 'Clerk', 'IT', 'Of Counsel'];

const SECTIONS = [
  {
    key: 'firm', label: 'Firm profile', icon: Building2,
    description: 'Name, logo, address, phone, website, practice areas',
    keywords: 'name logo address website phone email contact branding identity profile practice areas',
  },
  {
    key: 'team', label: 'Team & roles', icon: Users,
    description: 'Invite teammates, channels, role permissions',
    keywords: 'team invite employee staff role permission channel chat user',
  },
  {
    key: 'subscription', label: 'Subscription', icon: Crown,
    description: 'Plan, trial, payment, billing email',
    keywords: 'subscription plan upgrade pro enterprise trial payment paystack billing',
  },
  {
    key: 'billing', label: 'Billing defaults', icon: CreditCard,
    description: 'Invoice numbering, tax rates, default rates',
    keywords: 'invoice numbering tax vat rate hourly default currency',
  },
  {
    key: 'cases', label: 'Case management', icon: Briefcase,
    description: 'Matter retention, status defaults',
    keywords: 'matter case retention archive close',
  },
  {
    key: 'security', label: 'Security', icon: Shield,
    description: 'E2E encryption, DLP, push notifications, message retention, Bitcoin anchors, exports',
    keywords: 'encryption e2e password dlp push notification retention message integrity bitcoin anchor export trust',
  },
  {
    key: 'access', label: 'Access log', icon: History, leadersOnly: true,
    description: 'Sign-ins, password changes, super-admin events',
    keywords: 'audit log login logout sign-in password access history',
  },
  {
    // Permanent home for the sample-data wipe that was previously
    // only reachable from the onboarding checklist. Adds gettable
    // visibility for firms who loaded sample data months ago and
    // want to clean up.
    key: 'data', label: 'Data', icon: Database, leadersOnly: true,
    description: 'Sample / example records, cleanup tools',
    keywords: 'sample example seed wipe clean reset starter demo onboarding remove delete data',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
    >
      {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
      {copied ? 'Copied!' : 'Copy link'}
    </button>
  );
}

// Reusable save-feedback bar shown below each section's form
function SaveBar({ saving, saved, error, onSave, disabled }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <Button onClick={onSave} loading={saving} disabled={disabled || saving} icon={saved ? CheckCircle : Save}>
        {saved ? 'Saved!' : 'Save changes'}
      </Button>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── useSaveSection — encapsulates save/error/success state ────────────────────
function useSaveSection() {
  const [saving, setSaving]     = useState(false);
  const [saved,  setSaved]      = useState(false);
  const [error,  setError]      = useState('');

  const save = useCallback(async (fn) => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await fn();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, []);

  return { saving, saved, error, save };
}

// ── Firm profile section ──────────────────────────────────────────────────────
function FirmProfileSection({ firm, firmId, refresh }) {
  const [form, setForm] = useState({
    name:    '',
    email:   '',
    phone:   '',
    address: '',
    city:    '',
    state:   '',
    country: 'Ghana',
    website: '',
  });
  const { saving, saved, error, save } = useSaveSection();

  // Logo upload state
  const [logoPreview, setLogoPreview]   = useState(null);
  const [logoFile,    setLogoFile]       = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError,     setLogoError]    = useState('');
  const [logoSaved,     setLogoSaved]    = useState(false);

  useEffect(() => {
    if (firm) {
      setForm({
        name:    firm.name    || '',
        email:   firm.email   || '',
        phone:   firm.phone   || '',
        address: firm.address || '',
        city:    firm.city    || '',
        state:   firm.state   || '',
        country: firm.country || 'Ghana',
        website: firm.website || '',
      });
    }
  }, [firm]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setLogoError('Please upload a PNG, JPG, SVG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Logo must be under 2 MB.');
      return;
    }
    setLogoError('');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    setLogoError('');
    const { error: err } = await uploadFirmLogo(firmId, logoFile);
    setLogoUploading(false);
    if (err) {
      setLogoError(err.message || 'Upload failed. Make sure the "logos" storage bucket exists in Supabase.');
      return;
    }
    setLogoSaved(true);
    setLogoFile(null);
    setTimeout(() => setLogoSaved(false), 3000);
    refresh(); // pull fresh logo_url into FirmContext
  };

  const handleLogoRemove = async () => {
    setLogoPreview(null);
    setLogoFile(null);
    setLogoError('');
    // Clear logo_url in DB
    await updateFirm(firmId, { logo_url: null });
    refresh();
  };

  // Track whether the firm name is being changed — triggers a confirmation
  // modal because firm.name appears on every NEW invoice and receipt going
  // forward (existing documents keep their snapshot from migration 085).
  const renamingFirm = firm && form.name.trim() && form.name.trim() !== (firm.name || '').trim();
  const [showRenameConfirm, setShowRenameConfirm] = useState(false);

  const persistChanges = () =>
    save(async () => {
      const { error: err } = await updateFirm(firmId, form);
      if (err) throw err;
      refresh();
    });

  const handleSave = () => {
    if (renamingFirm) { setShowRenameConfirm(true); return; }
    persistChanges();
  };

  return (
    <>
    <Card>
      <CardHeader title="Firm profile" subtitle="This information appears on invoices and client-facing documents." />
      <CardBody className="space-y-6">
        {/* ── Logo upload ─────────────────────────────────────────────── */}
        <div>
          <Label>Firm logo</Label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Appears in the sidebar and on client-facing documents. PNG, JPG, SVG or WebP · max 2 MB.
          </p>
          <div className="flex items-center gap-4">
            {/* Preview */}
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800 flex-shrink-0">
              {logoPreview || firm?.logo_url ? (
                <img
                  src={logoPreview || firm?.logo_url}
                  alt="Firm logo"
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <ImageIcon size={24} className="text-slate-300 dark:text-slate-600" />
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    className="sr-only"
                    onChange={handleLogoChange}
                  />
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition cursor-pointer">
                    <Upload size={13} /> Choose image
                  </span>
                </label>

                {logoFile && (
                  <Button
                    size="sm"
                    loading={logoUploading}
                    onClick={handleLogoUpload}
                  >
                    {logoUploading ? 'Uploading…' : 'Upload'}
                  </Button>
                )}

                {(firm?.logo_url || logoPreview) && !logoFile && (
                  <button
                    onClick={handleLogoRemove}
                    className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition"
                  >
                    <X size={12} /> Remove
                  </button>
                )}
              </div>

              {logoFile && (
                <p className="text-xs text-slate-500 truncate max-w-xs">{logoFile.name}</p>
              )}
              {logoError && (
                <p className="text-xs text-red-600">{logoError}</p>
              )}
              {logoSaved && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle size={12} /> Logo saved!
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-800" />

        {/* ── Fields grid ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label required>Firm name</Label>
            <Input value={form.name} onChange={set('name')} placeholder="Mensah & Associates" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={form.email} onChange={set('email')} placeholder="info@yourfirm.com" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} placeholder="+233 20 000 0000" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={set('website')} placeholder="https://yourfirm.com" />
          </div>
          <div className="sm:col-span-2">
            <Label>Street address</Label>
            <Input value={form.address} onChange={set('address')} placeholder="123 Liberation Road" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={set('city')} placeholder="Accra" />
          </div>
          <div>
            <Label>Region</Label>
            <select
              value={form.state}
              onChange={set('state')}
              className="block w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700"
            >
              <option value="">Select region…</option>
              {GHANA_REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Country</Label>
            <Input value={form.country} onChange={set('country')} placeholder="Ghana" />
          </div>
        </div>

        <SaveBar saving={saving} saved={saved} error={error} onSave={handleSave} disabled={!form.name.trim()} />
      </CardBody>

      {/* Firm-name change confirmation — protects against accidental rebrands */}
      <Modal
        open={showRenameConfirm}
        onClose={() => setShowRenameConfirm(false)}
        title="Rename firm?"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRenameConfirm(false)} disabled={saving}>Cancel</Button>
            <Button
              loading={saving}
              onClick={async () => {
                await persistChanges();
                setShowRenameConfirm(false);
              }}
            >
              Yes, rename to "{form.name.trim()}"
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
          <p>
            You're changing the firm name from{' '}
            <strong className="text-slate-900 dark:text-white">{firm?.name || '—'}</strong> to{' '}
            <strong className="text-indigo-700 dark:text-indigo-300">{form.name.trim()}</strong>.
          </p>
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-xs text-amber-900 dark:text-amber-200 space-y-1">
            <div className="font-semibold">What changes</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Sidebar, portal, and intake forms switch to the new name immediately</li>
              <li>New invoices and receipts use the new name</li>
              <li>The portal magic-link slug stays the same so existing links keep working</li>
            </ul>
            <div className="font-semibold pt-1">What stays the same</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Already-issued invoices and receipts keep showing the original firm name (snapshotted at issue time)</li>
              <li>Already-sent client emails are unchanged</li>
            </ul>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            The rename is recorded in the activity log so other admins can see who renamed and when.
          </p>
        </div>
      </Modal>
    </Card>

    {/* Danger zone — firm-side self-close. Admin-only. Server-side
        enforcement and 30-day retention live in migration 133. */}
    <DangerZone firm={firm} />
    </>
  );
}

// ── Danger zone (self-close firm) ─────────────────────────────────────────────
function DangerZone({ firm }) {
  const { userProfile, logout } = useAuth();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [closing, setClosing] = useState(false);
  // `done` flips to true after closeMyFirm succeeds — swaps the modal
  // content from the confirmation form to the farewell screen. We don't
  // sign the user out automatically; the farewell needs to stay
  // readable so they understand what happened to their data + billing.
  // They click "Sign me out" when they're ready.
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Server-enforces this same check in migration 133 (raises 42501 if
  // role isn't 'Admin'). UI-side gate just keeps the button out of
  // sight for non-admins so they don't get confused.
  const isAdmin = userProfile?.role === 'Admin';
  if (!isAdmin) return null;

  const nameMatches =
    confirmName.trim().length > 0 &&
    confirmName.trim() === (firm?.name || '').trim();

  const handleClose = async () => {
    setError('');
    setClosing(true);
    const { error: rpcErr } = await closeMyFirm(confirmName, reason);
    setClosing(false);
    if (rpcErr) {
      setError(rpcErr.message || 'Could not close the firm.');
      return;
    }
    // Server has flipped suspended_at + deleted_at. The current_firm_id()
    // mask now hides all firm data from the user. Show the farewell
    // screen instead of immediately signing out — gives them time to
    // read what happened to their data + billing before they're booted.
    setDone(true);
  };

  const handleFinalLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    // Belt-and-suspenders: hard reload to home in case logout didn't
    // unmount the active page (eg. background timers still firing).
    window.location.href = '/';
  };

  return (
    <Card className="border-red-200 dark:border-red-900/60">
      <CardHeader
        title="Danger zone"
        subtitle="Closing the firm signs everyone out and blocks access to all firm data."
      />
      <CardBody>
        <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50/40 dark:bg-red-950/20 p-4 sm:p-5 space-y-3">
          <p className="text-sm font-semibold text-red-700 dark:text-red-300">
            Close <span className="text-slate-900 dark:text-white">{firm?.name || 'this firm'}</span>
          </p>
          <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 list-disc pl-4">
            <li>All firm members are signed out immediately and can no longer access matters, contacts, invoices or messages.</li>
            <li>Your data is preserved for <strong>30 days</strong>. If you change your mind, email support within that window to restore the firm.</li>
            <li>After 30 days the data may be permanently deleted. This step is irreversible.</li>
            <li>Existing invoices and receipts already issued to clients are unaffected — they keep their copies.</li>
          </ul>
          <Button
            variant="danger"
            icon={Trash2}
            onClick={() => { setConfirmName(''); setReason(''); setError(''); setShowCloseModal(true); }}
          >
            Close firm account
          </Button>
        </div>
      </CardBody>

      <Modal
        open={showCloseModal}
        // While closing OR after done we don't let the user dismiss
        // — we need them to read the farewell screen. After they click
        // "Sign me out" we hard-redirect anyway.
        onClose={() => !closing && !done && setShowCloseModal(false)}
        title={done ? 'Account closed' : 'Close firm account?'}
        size="md"
        footer={done ? (
          <Button variant="primary" onClick={handleFinalLogout}>
            Sign me out
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setShowCloseModal(false)} disabled={closing}>Cancel</Button>
            <Button
              variant="danger"
              loading={closing}
              disabled={!nameMatches || closing}
              onClick={handleClose}
            >
              Yes, close {firm?.name}
            </Button>
          </>
        )}
      >
        {done ? (
          // ── Farewell screen ───────────────────────────────────────
          // Shows AFTER closeMyFirm succeeds, BEFORE the user is logged
          // out. Gives them time to read what happened to their data
          // and what they need to do about their Paystack billing.
          <div className="space-y-5 text-sm text-slate-700 dark:text-slate-200">
            <div className="text-center py-2">
              <div className="inline-flex w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/40 items-center justify-center mb-3">
                <span className="text-2xl" aria-hidden>👋</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                We're sorry to see you go.
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {firm?.name} has been closed. Thanks for trying DocketFlow.
              </p>
            </div>

            {/* What happened to my data */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-900 dark:text-white uppercase tracking-wide">
                Your data
              </p>
              <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 list-disc pl-4">
                <li>Preserved for <strong>30 days</strong>. Changed your mind? Email <a href="mailto:support@docketflowapp.com" className="text-indigo-600 dark:text-indigo-400 underline">support@docketflowapp.com</a> from this account's email address and we'll restore everything.</li>
                <li>After 30 days the data is permanently deleted and can't be recovered.</li>
                <li>Invoices and receipts you've already sent to clients keep working — they have their own copies.</li>
              </ul>
            </div>

            {/* What about Paystack billing */}
            <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/20 p-4 space-y-2">
              <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
                One more thing — your Paystack subscription
              </p>
              <p className="text-xs text-slate-700 dark:text-slate-200">
                Closing your account here doesn't automatically cancel your monthly/annual Paystack subscription. So you don't get charged again next cycle, please either:
              </p>
              <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 list-disc pl-4">
                <li>Log into your Paystack dashboard and cancel the DocketFlow subscription, OR</li>
                <li>Email <a href="mailto:hello@docketflowapp.com?subject=Cancel%20my%20subscription" className="text-indigo-600 dark:text-indigo-400 underline">hello@docketflowapp.com</a> with the subject "Cancel my subscription" and we'll handle it within one business day.</li>
              </ul>
            </div>

            {/* Optional gentle ask */}
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              If anything we could've done better would have kept you, we'd love to hear it. Just reply to any of our emails or write to <a href="mailto:hello@docketflowapp.com" className="text-indigo-600 dark:text-indigo-400 underline">hello@docketflowapp.com</a>.
            </p>
          </div>
        ) : (
          // ── Pre-close confirmation form ───────────────────────────
          <div className="space-y-4 text-sm text-slate-700 dark:text-slate-200">
            <p>
              Type <strong className="text-slate-900 dark:text-white">{firm?.name}</strong> below to confirm. Everyone in the firm is signed out as soon as you click Close.
            </p>
            <div>
              <Label>Confirm firm name</Label>
              <Input
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={firm?.name || ''}
                autoFocus
              />
            </div>
            <div>
              <Label>Reason (optional, logged for our records)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. moving to another tool, firm dissolving, etc."
              />
            </div>
            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                {error}
              </div>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Data is recoverable for 30 days by emailing support@docketflowapp.com from the email address on this account.
            </p>
          </div>
        )}
      </Modal>
    </Card>
  );
}

// ── Team & roles section ──────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EMPLOYMENT_TYPES = [
  { value: 'full_time',  label: 'Full-time' },
  { value: 'part_time',  label: 'Part-time' },
  { value: 'contract',   label: 'Contract' },
  { value: 'intern',     label: 'Intern' },
  { value: 'of_counsel', label: 'Of Counsel' },
];

const PRACTICE_AREAS = [
  'Corporate & Commercial', 'Litigation', 'Criminal Law', 'Family Law',
  'Real Estate', 'Employment Law', 'Intellectual Property', 'Immigration',
  'Tax', 'Banking & Finance', 'Energy & Resources', 'Other',
];

const BLANK_STAFF = {
  firstName: '', middleName: '', lastName: '', email: '', confirmEmail: '',
  role: 'Associate', title: '', hourlyRate: '', practiceArea: '', employmentType: 'full_time',
};

// Visual tone for a role chip in the team directory. Keep the colour
// associations stable across the app so a Partner badge always looks
// the same way regardless of which page is rendering it.
//   Admin     — amber  (most powerful firm role)
//   Partner   — indigo (senior firm role)
//   Attorney  — sky    (mid-tier attorney)
//   Associate — blue   (junior attorney)
//   Paralegal — green  (support staff)
//   Clerk     — emerald(support staff, lighter than paralegal)
//   IT        — purple (specialist operations role, visually distinct)
const roleTone = (role) => {
  const map = {
    Admin:       'amber',
    Partner:     'indigo',
    Attorney:    'sky',
    Associate:   'blue',
    Paralegal:   'green',
    Clerk:       'emerald',
    IT:          'purple',
    'Of Counsel':'slate',
  };
  return map[role] || 'slate';
};

// ── Shared staff form fields (used in both "Invite" and "Add staff" modals) ──
function StaffFormFields({ form, setForm, showEmail, showConfirmEmail, requireEmail, touched, setTouched }) {
  const field = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const touch = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const firstErr   = !form.firstName.trim() ? 'First name is required' : '';
  const lastErr    = !form.lastName.trim()  ? 'Last name is required'  : '';
  const emailErr   = requireEmail && !EMAIL_RE.test(form.email.trim()) ? 'Enter a valid email' : '';
  const confirmErr = showConfirmEmail
    ? (form.confirmEmail.trim() !== form.email.trim() ? 'Emails do not match' : emailErr)
    : '';

  return (
    <div className="space-y-4">
      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label required>First name</Label>
          <Input value={form.firstName} onChange={field('firstName')} onBlur={touch('firstName')} placeholder="Kwame" />
          {touched.firstName && firstErr && <p className="mt-1 text-xs text-red-500">⚠ {firstErr}</p>}
        </div>
        <div>
          <Label required>Last name</Label>
          <Input value={form.lastName} onChange={field('lastName')} onBlur={touch('lastName')} placeholder="Mensah" />
          {touched.lastName && lastErr && <p className="mt-1 text-xs text-red-500">⚠ {lastErr}</p>}
        </div>
      </div>
      <div>
        <Label>Middle name <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
        <Input value={form.middleName} onChange={field('middleName')} placeholder="Optional" />
      </div>

      {/* Email */}
      {showEmail && (
        <>
          <div>
            <Label required={requireEmail}>Email address</Label>
            <Input type="email" value={form.email} onChange={field('email')} onBlur={touch('email')}
              placeholder="kwame@example.com" />
            {touched.email && emailErr && <p className="mt-1 text-xs text-red-500">⚠ {emailErr}</p>}
          </div>
          {showConfirmEmail && (
            <div>
              <Label required>Confirm email</Label>
              <Input type="email" value={form.confirmEmail} onChange={field('confirmEmail')} onBlur={touch('confirmEmail')}
                placeholder="kwame@example.com" />
              {touched.confirmEmail && confirmErr && <p className="mt-1 text-xs text-red-500">⚠ {confirmErr}</p>}
            </div>
          )}
        </>
      )}

      {/* Title + Role */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Title</Label>
          <Input value={form.title} onChange={field('title')} placeholder="e.g. Senior Associate" />
        </div>
        <div>
          <Label>Role</Label>
          <select value={form.role} onChange={field('role')}
            className="block w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {/* Employment type + Practice area */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Employment type</Label>
          <select value={form.employmentType} onChange={field('employmentType')}
            className="block w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none">
            {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label>Practice area</Label>
          <select value={form.practiceArea} onChange={field('practiceArea')}
            className="block w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none">
            <option value="">— Select —</option>
            {PRACTICE_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Billing rate */}
      <div>
        <Label>Billing rate <span className="text-slate-400 font-normal text-xs">(GH₵ / hour)</span></Label>
        <Input type="number" min="0" value={form.hourlyRate} onChange={field('hourlyRate')} placeholder="0.00" />
      </div>
    </div>
  );
}

// ─── Archived channels card ─────────────────────────────────────────────
// Collapsible list of channels that have been archived. Renders only when
// there's at least one row, so it doesn't take up space for firms that
// haven't archived anything. Firm leaders can restore a channel which
// brings it back to the sidebar.
function ArchivedChannelsCard({ firmId }) {
  const [rows,    setRows]    = useState([]);
  const [loaded,  setLoaded]  = useState(false);
  const [open,    setOpen]    = useState(false);
  const [busy,    setBusy]    = useState(null);

  const load = useCallback(() => {
    if (!firmId) return;
    listArchivedChannels(firmId).then(({ data }) => {
      setRows(Array.isArray(data) ? data : []);
      setLoaded(true);
    });
  }, [firmId]);

  useEffect(() => { load(); }, [load]);

  if (!loaded || rows.length === 0) return null;

  const restore = async (ch) => {
    if (!window.confirm(`Restore #${ch.name}? It will reappear in the sidebar and members can post again.`)) return;
    setBusy(ch.id);
    const { error } = await archiveFirmChannel(ch.id, false);
    setBusy(null);
    if (error) { toast.error('Could not restore: ' + error.message); return; }
    setRows((prev) => prev.filter((r) => r.id !== ch.id));
    toast.success(`#${ch.name} restored.`);
  };

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
      >
        <div className="flex items-center gap-2">
          <Archive size={14} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Archived channels
          </span>
          <span className="text-xs text-slate-500">({rows.length})</span>
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((c) => {
            const archiverName = c.archiver
              ? [c.archiver.first_name, c.archiver.last_name].filter(Boolean).join(' ').trim() || c.archiver.email
              : '—';
            return (
              <div key={c.id} className="px-6 py-3 flex items-center gap-3">
                {c.is_private
                  ? <UserPlus size={13} className="text-slate-400 flex-shrink-0" />
                  : <Hash size={13} className="text-slate-400 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 dark:text-slate-100">{c.is_private ? c.name : `#${c.name}`}</p>
                  <p className="text-xs text-slate-500">
                    Archived {formatDate(c.archived_at, { includeTime: true })} by {archiverName}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={RotateCcw}
                  disabled={busy === c.id}
                  onClick={() => restore(c)}
                >
                  {busy === c.id ? 'Restoring…' : 'Restore'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function TeamSection({ firm, firmId }) {
  const { userProfile } = useAuth();
  const [, navigate]    = useRoute();

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [authUsers,  setAuthUsers]  = useState([]);  // public.users (have/had login)
  const [employees,  setEmployees]  = useState([]);  // all employees (including staff-only)
  const [firmRoles,  setFirmRoles]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  // Seat-cap info: { seat_limit, in_use, available, is_full } | null
  // null means "no enforcement configured" (migration 141 not deployed or
  // unlimited plan) — UI hides the badge entirely in that case.
  const [seatInfo,   setSeatInfo]   = useState(null);
  // Storage-cap info: { used_mb, limit_mb, addon_mb, available_mb,
  //                     is_full, is_near_full } | null
  // Same null-means-hide semantics as seatInfo.
  const [storageInfo, setStorageInfo] = useState(null);

  const reload = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    const [uRes, eRes, rRes, sRes, stRes] = await Promise.all([
      listFirmUsers(firmId),
      listEmployees(firmId),
      listFirmRoles(firmId),
      getMyFirmSeatInfo(),
      getMyFirmStorageInfo(),
    ]);
    // Surface RPC errors so future debugging isn't blind. The page used
    // to silently coerce errors → empty array, which led to the bug where
    // every employee appeared under "STAFF RECORDS — NO SYSTEM ACCESS"
    // because authUsers came back empty without anyone noticing.
    if (uRes.error) {
      // eslint-disable-next-line no-console
      console.warn('[Settings/Team] list_my_firm_users error:', uRes.error);
    }
    setAuthUsers(uRes.data  || []);
    setEmployees(eRes.data  || []);
    setFirmRoles(rRes.data  || []);
    setSeatInfo(sRes.data || null);
    setStorageInfo(stRes.data || null);
    setLoading(false);
  }, [firmId]);

  useEffect(() => { reload(); }, [reload]);

  // ── Derived lists ─────────────────────────────────────────────────────────────
  //
  // Defense in depth against the `list_my_firm_users` RPC misbehaving.
  // History: migration 138 added that RPC to bypass `current_firm_id()`
  // drift. Several customer deployments have hit cases where the RPC
  // returns [] anyway (PostgREST cache miss, runtime exception swallowed,
  // a future migration introducing a regression, etc.) — and when that
  // happens this page used to dump every employee under "STAFF RECORDS —
  // NO SYSTEM ACCESS" because authUsers was empty.
  //
  // Fix: classify employees using `employees.user_id` as the source of
  // truth for "has a system login". The employees table is fetched from
  // a separate RPC (`list_firm_employees`, migration 120) so a failure
  // mode in `list_my_firm_users` can't also break this classifier. If
  // the auth-users-side RPC happens to fail entirely, we synthesize the
  // user-row shape from the employee row so the page still renders the
  // role dropdown for linked employees.
  const authUserByEmail = new Map(
    authUsers
      .filter((u) => u.email)
      .map((u) => [u.email.toLowerCase(), u]),
  );
  const authUserById = new Map(authUsers.map((u) => [u.id, u]));

  // Resolve role_id from the role name. Needed because list_my_firm_users
  // (migration 138) returns role text but not role_id — and the dropdown
  // below reads role_id to know which option is selected. Without this
  // resolution, every dropdown shows "Select role" instead of the user's
  // current role until you change it (purely cosmetic, but confusing).
  const roleIdByName = new Map(
    firmRoles
      .filter((r) => r.name)
      .map((r) => [r.name.toLowerCase(), r.id]),
  );
  const resolveRoleId = (u) => {
    if (u.role_id) return u.role_id;
    if (!u.role)   return null;
    return roleIdByName.get(u.role.toLowerCase()) || null;
  };

  // System users = every employee with a user_id link OR every authUser
  // (covers the edge case where an authUser exists without an employees
  // row, e.g. the founder before they've added themselves as staff).
  const linkedEmployees = employees.filter((e) => e.user_id);
  const linkedEmployeeIds = new Set(linkedEmployees.map((e) => e.user_id));

  const systemUsersRaw = [
    // 1. Linked employees — prefer real authUser data, fall back to a
    //    synthesized row built from the employee. Synthesized rows are
    //    flagged so the UI can show "Awaiting user data" semantics if
    //    we ever decide to differentiate; for now they render as Active.
    ...linkedEmployees.map((e) => {
      const real = authUserById.get(e.user_id);
      if (real) return { ...real, role: real.role || e.role };
      return {
        id:              e.user_id,
        first_name:      e.first_name,
        last_name:       e.last_name,
        email:           e.email,
        role:            e.role,
        role_id:         e.role_id || null,
        status:          'active',
        last_login:      null,
        created_at:      e.created_at,
        email_confirmed: true,
        _synthesized:    true,
      };
    }),
    // 2. AuthUsers that don't correspond to a linked employee (rare —
    //    typically pending invites where the employees row was created
    //    but user_id hasn't been backfilled yet, or the founder whose
    //    employees row was added after auth signup).
    ...authUsers.filter((u) => !linkedEmployeeIds.has(u.id)),
  ];
  // Enrich with role_id so the dropdown shows the current selection.
  const systemUsers = systemUsersRaw.map((u) => ({
    ...u,
    role_id: resolveRoleId(u),
  }));

  // A user counts as "active" if either email_confirmed is true OR they have
  // a last_login. The flag historically went stale when the auth.users
  // trigger missed an update; migration 103 backfills + self-heals, but we
  // also tolerate the stale-flag case in the UI so nothing falls through.
  const isActive = (u) => u.email_confirmed === true || !!u.last_login;
  const activeMembers = systemUsers.filter(isActive);
  // Pending invites: auth user row exists (we got their email out the door)
  // but they haven't confirmed AND haven't logged in.
  const pendingUsers  = systemUsers.filter((u) => !isActive(u));
  // Staff without login: employees with no user_id link AND no matching
  // pending auth-user row. The user_id check is the primary discriminator —
  // it works even when listFirmUsers misbehaves.
  const staffOnly = employees.filter((emp) => {
    if (emp.user_id) return false; // linked → has system login → not staff-only
    if (!emp.email)  return true;  // no email + no user_id → truly staff-only
    // Has email but no user_id: check if there's a pending auth-user row.
    return !authUserByEmail.has(emp.email.toLowerCase());
  });

  // ── Role change (active members) ──────────────────────────────────────────────
  const [roleWorking, setRoleWorking] = useState({});
  const handleRoleIdChange = async (userId, roleId) => {
    setRoleWorking((w) => ({ ...w, [userId]: true }));
    const { error } = await setUserRole(userId, roleId);
    if (!error) {
      const role = firmRoles.find((r) => r.id === roleId);
      // Update both authUsers (real RPC path) and employees (synthesized
      // fallback path) so the UI reflects the new role no matter which
      // source the rendered row was built from. setAuthUsers is a no-op
      // if the user wasn't in the RPC result; setEmployees catches that
      // case via the employees.user_id link.
      setAuthUsers((ms) => ms.map((m) =>
        m.id === userId
          ? { ...m, role_id: roleId, role: role?.name || m.role }
          : m,
      ));
      setEmployees((es) => es.map((e) =>
        e.user_id === userId
          ? { ...e, role_id: roleId, role: role?.name || e.role }
          : e,
      ));
    }
    setRoleWorking((w) => ({ ...w, [userId]: false }));
  };

  // ── Delete / remove ───────────────────────────────────────────────────────────
  const [deletingMember, setDeletingMember] = useState(null);
  const [deleting,       setDeleting]       = useState(false);
  const [deleteError,    setDeleteError]    = useState('');

  const handleDeleteMember = async () => {
    if (!deletingMember) return;
    setDeleting(true);
    setDeleteError('');
    const { error } = await deleteTeamMember(deletingMember.id);
    setDeleting(false);
    if (error) { setDeleteError(error.message); return; }
    setAuthUsers((ms) => ms.filter((x) => x.id !== deletingMember.id));
    setDeletingMember(null);
  };

  const handleRemovePending = (u) => {
    if (!window.confirm(`Remove the pending invite for ${u.email}? This lets you re-invite them with a different email.`)) return;
    removeUnconfirmedUser(u.id).then(({ error }) => {
      if (error) { toast.error('Could not remove: ' + error.message); return; }
      setAuthUsers((ms) => ms.filter((x) => x.id !== u.id));
    });
  };

  const handleRemoveStaff = (emp) => {
    if (!window.confirm(`Remove staff record for ${emp.first_name} ${emp.last_name}?`)) return;
    deleteEmployee(emp.id).then(({ error }) => {
      if (error) { toast.error('Could not remove: ' + error.message); return; }
      setEmployees((es) => es.filter((x) => x.id !== emp.id));
    });
  };

  // ── Resend invite ─────────────────────────────────────────────────────────────
  const [resending, setResending] = useState({});
  const handleResend = async (u) => {
    setResending((r) => ({ ...r, [u.id]: true }));
    // Find the matching employees row for richer metadata
    const emp = employees.find((e) => e.email?.toLowerCase() === u.email?.toLowerCase());
    const { error } = await resendInvite(firmId, emp || {
      email: u.email, first_name: u.first_name, last_name: u.last_name,
      role: u.role, middle_name: '', title: '', hourly_rate: 0, practice_area: '', employment_type: 'full_time',
    });
    setResending((r) => ({ ...r, [u.id]: false }));
    if (error) toast.error('Could not resend: ' + error.message);
    else toast.success(`Invite resent to ${u.email}`);
  };

  // ── Invite modal (system login) ───────────────────────────────────────────────
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState(BLANK_STAFF);
  const [inviting,   setInviting]   = useState(false);
  const [inviteMsg,  setInviteMsg]  = useState(null);
  const [invTouched, setInvTouched] = useState({});

  const inviteValid =
    inviteForm.firstName.trim() &&
    inviteForm.lastName.trim() &&
    EMAIL_RE.test(inviteForm.email.trim()) &&
    inviteForm.email.trim() === inviteForm.confirmEmail.trim();

  const handleSendInvite = async () => {
    setInvTouched({ firstName: true, lastName: true, email: true, confirmEmail: true });
    if (!inviteValid) return;
    setInviting(true);
    setInviteMsg(null);
    const { error } = await inviteTeamMember(firmId, {
      email:          inviteForm.email.trim(),
      firstName:      inviteForm.firstName.trim(),
      middleName:     inviteForm.middleName.trim(),
      lastName:       inviteForm.lastName.trim(),
      role:           inviteForm.role,
      title:          inviteForm.title.trim(),
      hourlyRate:     parseFloat(inviteForm.hourlyRate) || 0,
      practiceArea:   inviteForm.practiceArea,
      employmentType: inviteForm.employmentType,
    });
    setInviting(false);
    if (error) {
      setInviteMsg({ type: 'error', text: error.message });
    } else {
      setInviteMsg({ type: 'success', text: `Invite sent to ${inviteForm.email.trim()}!` });
      setInviteForm(BLANK_STAFF);
      setInvTouched({});
      reload();
    }
  };

  const closeInvite = () => { setShowInvite(false); setInviteMsg(null); setInviteForm(BLANK_STAFF); setInvTouched({}); };

  // ── Add staff record modal (no login) ─────────────────────────────────────────
  const [showStaff,  setShowStaff]  = useState(false);
  const [staffForm,  setStaffForm]  = useState(BLANK_STAFF);
  const [addingStaff, setAddingStaff] = useState(false);
  const [staffMsg,   setStaffMsg]   = useState(null);
  const [sfTouched,  setSfTouched]  = useState({});

  const staffValid = staffForm.firstName.trim() && staffForm.lastName.trim();

  const handleAddStaff = async () => {
    setSfTouched({ firstName: true, lastName: true });
    if (!staffValid) return;
    setAddingStaff(true);
    setStaffMsg(null);
    const { error } = await addStaffRecord(firmId, {
      firstName:      staffForm.firstName.trim(),
      middleName:     staffForm.middleName.trim(),
      lastName:       staffForm.lastName.trim(),
      email:          staffForm.email.trim(),
      role:           staffForm.role,
      title:          staffForm.title.trim(),
      hourlyRate:     parseFloat(staffForm.hourlyRate) || 0,
      practiceArea:   staffForm.practiceArea,
      employmentType: staffForm.employmentType,
    });
    setAddingStaff(false);
    if (error) {
      setStaffMsg({ type: 'error', text: error.message });
    } else {
      setStaffMsg({ type: 'success', text: `${staffForm.firstName} ${staffForm.lastName} added to the directory.` });
      setStaffForm(BLANK_STAFF);
      setSfTouched({});
      reload();
    }
  };

  const closeStaff = () => { setShowStaff(false); setStaffMsg(null); setStaffForm(BLANK_STAFF); setSfTouched({}); };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Invite modal (system login) ───────────────────────────────────── */}
      <Modal open={showInvite} onClose={closeInvite} title="Invite team member" size="md"
        footer={
          <>
            <Button variant="ghost" onClick={closeInvite}>Cancel</Button>
            <Button icon={Mail} loading={inviting} onClick={handleSendInvite}>Send invite</Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            They'll receive an email with a link to set up their password and join <strong>{firm?.name}</strong>.
          </p>
          {inviteMsg && (
            <div className={`rounded-lg px-4 py-3 text-sm ${inviteMsg.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
              {inviteMsg.text}
            </div>
          )}
          <StaffFormFields form={inviteForm} setForm={setInviteForm}
            showEmail showConfirmEmail requireEmail
            touched={invTouched} setTouched={setInvTouched} />
        </div>
      </Modal>

      {/* ── Add staff record modal (no login) ────────────────────────────── */}
      <Modal open={showStaff} onClose={closeStaff} title="Add staff record" size="md"
        footer={
          <>
            <Button variant="ghost" onClick={closeStaff}>Cancel</Button>
            <Button icon={UserPlus} loading={addingStaff} onClick={handleAddStaff}>Add to directory</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
            This adds a staff record to the directory <strong>without</strong> granting login access.
            Use this for support staff, contractors, or anyone tracked in HR who doesn't need to log in.
            You can grant login access later by inviting them.
          </div>
          {staffMsg && (
            <div className={`rounded-lg px-4 py-3 text-sm ${staffMsg.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
              {staffMsg.text}
            </div>
          )}
          <StaffFormFields form={staffForm} setForm={setStaffForm}
            showEmail={true} showConfirmEmail={false} requireEmail={false}
            touched={sfTouched} setTouched={setSfTouched} />
        </div>
      </Modal>

      {/* ── Storage usage card ───────────────────────────────────────────────
          Customer-facing storage status: bar + breakdown + "Buy more"
          CTA for Admins/Partners. Hidden entirely if storageInfo is
          null (enterprise / no cap configured). */}
      {storageInfo && (
        <StorageUsageCard
          storageInfo={storageInfo}
          canPurchase={['Admin', 'Owner', 'Partner'].includes(userProfile?.role || '')}
        />
      )}

      {/* ── Directory card ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="Team directory"
          subtitle={
            seatInfo && seatInfo.seat_limit != null
              ? `${seatInfo.in_use} of ${seatInfo.seat_limit} seats used` +
                (seatInfo.is_full
                  ? ' — contact your administrator to add more seats.'
                  : '')
              : 'Manage system access, pending invites, and all staff records.'
          }
          action={
            <div className="flex items-center gap-2 flex-wrap">
              {/* Seat-usage badge — visible only when a cap is configured */}
              {seatInfo && seatInfo.seat_limit != null && (
                <span
                  className={
                    'inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ' +
                    (seatInfo.is_full
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : seatInfo.available <= 2
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300')
                  }
                  title={
                    seatInfo.is_full
                      ? 'All seats are in use. Suspend or remove someone, or contact your administrator to add seats.'
                      : `${seatInfo.available} seat(s) remaining`
                  }
                >
                  {seatInfo.in_use}/{seatInfo.seat_limit} seats
                </span>
              )}
              {/* Storage badge was here. Replaced by the dedicated
                  StorageUsageCard above the directory — storage isn't
                  a team-management concern and the badge in this
                  toolbar was both cramped and easy to miss. */}
              <Button
                variant="ghost"
                icon={UserPlus}
                onClick={() => setShowStaff(true)}
                disabled={seatInfo?.is_full}
                title={seatInfo?.is_full ? 'Seat limit reached — free a seat first.' : undefined}
              >
                Add staff
              </Button>
              <Button
                variant="secondary"
                icon={UsersRound}
                onClick={() => navigate('/employees/invite')}
                disabled={seatInfo?.is_full}
                title={seatInfo?.is_full ? 'Seat limit reached — free a seat first.' : undefined}
              >
                Bulk invite
              </Button>
              <Button
                icon={Mail}
                onClick={() => setShowInvite(true)}
                disabled={seatInfo?.is_full}
                title={seatInfo?.is_full ? 'Seat limit reached — free a seat first.' : undefined}
              >
                Invite &amp; grant access
              </Button>
            </div>
          }
        />
        <CardBody className="p-0">
          {loading ? (
            <p className="text-sm text-slate-500 p-6">Loading team…</p>
          ) : authUsers.length === 0 && staffOnly.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={28} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No team members yet.</p>
              <button onClick={() => setShowInvite(true)} className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                Send your first invite
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">

              {/* ── Active members ─────────────────────────────────────── */}
              {activeMembers.length > 0 && (
                <div>
                  <div className="px-6 py-2 bg-slate-50 dark:bg-slate-800/40">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Active members ({activeMembers.length})
                    </p>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {activeMembers.map((m) => {
                        const isMe = m.id === userProfile?.id;
                        return (
                          <tr key={m.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition">
                            <td className="px-6 py-3 w-full">
                              <p className="font-medium text-slate-900 dark:text-slate-100">
                                {fullName(m) || m.email}
                                {isMe && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">{m.email}</p>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {isMe ? (
                                <Badge tone={roleTone(m.role)}>{m.role}</Badge>
                              ) : (
                                <div className="relative inline-block">
                                  <select
                                    value={m.role_id || ''}
                                    disabled={roleWorking[m.id]}
                                    onChange={(e) => handleRoleIdChange(m.id, e.target.value)}
                                    className="appearance-none pl-2.5 pr-7 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-50"
                                  >
                                    <option value="">Select role</option>
                                    {firmRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                  </select>
                                  <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap hidden md:table-cell">
                              {m.last_login ? `Last login ${formatDate(m.last_login)}` : 'Never logged in'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {!isMe && (
                                <button
                                  title="Remove member"
                                  onClick={() => { setDeleteError(''); setDeletingMember(m); }}
                                  className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pending invites ────────────────────────────────────── */}
              {pendingUsers.length > 0 && (
                <div>
                  <div className="px-6 py-2 bg-amber-50 dark:bg-amber-900/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      Pending invites ({pendingUsers.length})
                    </p>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {pendingUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition opacity-80">
                          <td className="px-6 py-3 w-full">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 dark:text-slate-100">
                                {fullName(u) || u.email}
                              </p>
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                <Clock size={9} /> Awaiting sign-up
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">{u.email}</p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge tone="amber">{u.role || 'Invited'}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                title="Resend invite email"
                                disabled={resending[u.id]}
                                onClick={() => handleResend(u)}
                                className="px-2.5 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
                              >
                                {resending[u.id] ? 'Sending…' : 'Resend'}
                              </button>
                              <button
                                title="Cancel invite"
                                onClick={() => handleRemovePending(u)}
                                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Staff without login ────────────────────────────────── */}
              {staffOnly.length > 0 && (
                <div>
                  <div className="px-6 py-2 bg-slate-50 dark:bg-slate-800/40">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Staff records — no system access ({staffOnly.length})
                    </p>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {staffOnly.map((emp) => (
                        <tr key={emp.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition">
                          <td className="px-6 py-3 w-full">
                            <p className="font-medium text-slate-900 dark:text-slate-100">
                              {[emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(' ')}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {emp.email || 'No email on record'}
                              {emp.title ? ` · ${emp.title}` : ''}
                              {emp.practice_area ? ` · ${emp.practice_area}` : ''}
                            </p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge tone={roleTone(emp.role)}>{emp.role}</Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                            <span className="text-xs text-slate-400">
                              {EMPLOYMENT_TYPES.find((t) => t.value === emp.employment_type)?.label || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              {emp.email && (
                                <button
                                  title="Grant login access"
                                  onClick={() => {
                                    setInviteForm((f) => ({
                                      ...f,
                                      firstName:      emp.first_name || '',
                                      middleName:     emp.middle_name || '',
                                      lastName:       emp.last_name || '',
                                      email:          emp.email || '',
                                      confirmEmail:   emp.email || '',
                                      role:           emp.role || 'Associate',
                                      title:          emp.title || '',
                                      hourlyRate:     emp.hourly_rate ? String(emp.hourly_rate) : '',
                                      practiceArea:   emp.practice_area || '',
                                      employmentType: emp.employment_type || 'full_time',
                                    }));
                                    setShowInvite(true);
                                  }}
                                  className="px-2.5 py-1 text-xs rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 transition"
                                >
                                  Grant access
                                </button>
                              )}
                              <button
                                title="Remove staff record"
                                onClick={() => handleRemoveStaff(emp)}
                                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Archived channels (collapsible) ─────────────────────────────── */}
      <ArchivedChannelsCard firmId={firmId} />

      {/* ── Delete confirmed member modal ─────────────────────────────────── */}
      {deletingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">Remove team member</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Remove <span className="font-semibold">{fullName(deletingMember) || deletingMember.email}</span> from the firm?
              Their login account will be permanently deleted and they will lose all access immediately.
            </p>
            {deleteError && (
              <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{deleteError}</p>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => { setDeletingMember(null); setDeleteError(''); }}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMember}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? 'Removing…' : 'Yes, remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Subscription section ──────────────────────────────────────────────────────
function SubscriptionSection({ firm }) {
  const { subscriptionStatus, isTrial, daysRemaining } = useFirm();
  const { planName } = usePlan();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const planLabel = {
    trial:     'Trial',
    active:    'Active',
    suspended: 'Suspended',
  }[subscriptionStatus] || subscriptionStatus;

  const planTone = {
    trial:     'amber',
    active:    'green',
    suspended: 'red',
  }[subscriptionStatus] || 'slate';

  return (
    <>
      <Card>
        <CardHeader title="Subscription" subtitle="Your current plan and billing status." />
        <CardBody className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Current plan</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {isTrial
                  ? `${planName} (Trial) — ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
                  : planName}
              </p>
            </div>
            <Badge tone={planTone}>{planLabel}</Badge>
          </div>

          {(isTrial || subscriptionStatus === 'suspended') && (
            <Alert tone={subscriptionStatus === 'suspended' ? 'error' : 'warning'}>
              {subscriptionStatus === 'suspended'
                ? 'Your trial has expired. Upgrade to restore access.'
                : `Your free trial expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Upgrade to keep access.`}
            </Alert>
          )}

          <div className="flex gap-2">
            <Button onClick={() => setShowUpgrade(true)} icon={Crown}>
              {subscriptionStatus === 'active' ? 'Change plan' : 'Upgrade now'}
            </Button>
          </div>

          {firm?.trial_ends_at && (
            <div className="border-t border-slate-100 dark:border-slate-800 pt-4 text-xs text-slate-500 space-y-1">
              <p>Trial started: {formatDate(firm.trial_started_at)}</p>
              <p>Trial ends: {formatDate(firm.trial_ends_at)}</p>
            </div>
          )}
        </CardBody>
      </Card>

      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </>
  );
}

// ── Billing defaults section ──────────────────────────────────────────────────
function BillingSection({ firm, firmId, refresh }) {
  const [form, setForm] = useState({
    payment_terms_days: 30,
    late_fee_percent:   1.5,
    invoice_prefix:     'INV-',
    currency:           'GHS',
  });
  const { saving, saved, error, save } = useSaveSection();

  useEffect(() => {
    if (firm) {
      setForm({
        payment_terms_days: firm.payment_terms_days ?? 30,
        late_fee_percent:   firm.late_fee_percent   ?? 1.5,
        invoice_prefix:     firm.invoice_prefix     || 'INV-',
        currency:           firm.currency           || 'GHS',
      });
    }
  }, [firm]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = () =>
    save(async () => {
      const { error: err } = await updateFirm(firmId, {
        payment_terms_days: Number(form.payment_terms_days),
        late_fee_percent:   Number(form.late_fee_percent),
        invoice_prefix:     form.invoice_prefix,
        currency:           form.currency,
      });
      if (err) throw err;
      refresh();
    });

  return (
    <Card>
      <CardHeader
        title="Billing defaults"
        subtitle="These defaults apply to all new invoices. You can override them per invoice."
      />
      <CardBody className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Payment terms (days)</Label>
            <Input type="number" min={0} value={form.payment_terms_days} onChange={set('payment_terms_days')} />
          </div>
          <div>
            <Label>Late fee (%)</Label>
            <Input type="number" min={0} step="0.1" value={form.late_fee_percent} onChange={set('late_fee_percent')} />
          </div>
          <div>
            <Label>Invoice number prefix</Label>
            <Input value={form.invoice_prefix} onChange={set('invoice_prefix')} placeholder="INV-" />
          </div>
          <div>
            <Label>Currency</Label>
            <Select
              value={form.currency}
              onChange={set('currency')}
              options={[
                { value: 'GHS', label: 'GHS — Ghana Cedi (₵)' },
                { value: 'USD', label: 'USD — US Dollar ($)' },
                { value: 'EUR', label: 'EUR — Euro (€)' },
                { value: 'GBP', label: 'GBP — British Pound (£)' },
              ]}
            />
          </div>
        </div>
        <SaveBar saving={saving} saved={saved} error={error} onSave={handleSave} />
      </CardBody>
    </Card>
  );
}

// ── Security section (informational) ─────────────────────────────────────────
function SecuritySection() {
  return (
    <Card>
      <CardHeader title="Security & compliance" />
      <CardBody className="space-y-4 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Row-level security</p>
            <p className="text-xs text-slate-500 mt-0.5">All firm data is isolated — no other firm can access your records.</p>
          </div>
          <Badge tone="green">Enabled</Badge>
        </div>
        <div className="border-t border-slate-100 dark:border-slate-800 pt-4 flex items-center justify-between">
          <div>
            <p className="font-medium">Multi-factor authentication</p>
            <p className="text-xs text-slate-500 mt-0.5">Add a second factor to protect your account. Managed via Supabase Auth.</p>
          </div>
          <Badge tone="slate">Coming soon</Badge>
        </div>
        <PushNotificationsRow />
        <DlpPolicyRow />
        <E2EEncryptionRow />
        <MessageRetentionRow />
        <MessagingIntegrityRow />
        <BitcoinAnchorsRow />
        <MessagingExportRow />
      </CardBody>
    </Card>
  );
}

// Browser push notifications — per-device opt-in. Each call to Enable
// runs through the OS permission prompt and registers a new endpoint with
// the server. Users with multiple devices/browsers can enable on each one.
function PushNotificationsRow() {
  const { supported, configured, permission, subscribed, error, enable, disable, sendTest } = useNotifications();
  const [busy, setBusy] = useState(false);

  const enableNow = async () => { setBusy(true); await enable(); setBusy(false); };
  const disableNow = async () => { setBusy(true); await disable(); setBusy(false); };

  const stateLabel = !supported
    ? 'Not supported in this browser'
    : !configured
      ? 'Server not configured'
      : permission === 'denied'
        ? 'Blocked by browser'
        : subscribed
          ? 'Enabled on this device'
          : 'Off';

  const tone = subscribed
    ? 'green'
    : permission === 'denied' || !supported || !configured
      ? 'slate'
      : 'amber';

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">Browser push notifications</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Get a system notification when someone @-mentions you or sends you a direct message —
            even when DocketFlow isn't open. Enable on every device you use (laptop + phone).
            Install the app to your home screen on mobile (browser menu → "Add to Home Screen") for
            the full DocketFlow experience.
          </p>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
        <Badge tone={tone}>{stateLabel}</Badge>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {subscribed ? (
          <>
            <Button variant="ghost" onClick={sendTest}>Send test</Button>
            <Button onClick={disableNow} disabled={busy}>
              {busy ? 'Working…' : 'Disable on this device'}
            </Button>
          </>
        ) : (
          <Button onClick={enableNow} disabled={busy || !supported || !configured || permission === 'denied'}>
            {busy ? 'Working…' : 'Enable notifications'}
          </Button>
        )}
      </div>
    </div>
  );
}

// Tamper-evident sealing of the team chat archive. Lets a firm leader run
// a verification across all channels and capture a new seal anchor.
function MessagingIntegrityRow() {
  const { firmId } = useFirm();
  const [verifying, setVerifying]   = useState(false);
  const [sealing,   setSealing]     = useState(false);
  const [emailing,  setEmailing]    = useState(false);
  const [emailMsg,  setEmailMsg]    = useState('');
  const [report,    setReport]      = useState(null);
  const [latestSeals, setLatestSeals] = useState({}); // channel_id → newest seal row
  const [error,     setError]       = useState('');

  const refreshSeals = useCallback(async () => {
    if (!firmId) return;
    const { data } = await listLatestSeals(firmId);
    if (!data) return;
    // Pick the latest seal per channel.
    const m = {};
    for (const s of data) {
      if (!m[s.channel_id] || new Date(s.captured_at) > new Date(m[s.channel_id].captured_at)) {
        m[s.channel_id] = s;
      }
    }
    setLatestSeals(m);
  }, [firmId]);

  useEffect(() => { refreshSeals(); }, [refreshSeals]);

  const verify = async () => {
    setVerifying(true); setError(''); setReport(null);
    const { data, error: err } = await verifyFirmIntegrity();
    setVerifying(false);
    if (err) { setError(err.message || 'Verification failed'); return; }
    setReport(Array.isArray(data) ? data : []);
  };

  const seal = async () => {
    setSealing(true); setError('');
    const { error: err } = await recordMessagingSeals();
    setSealing(false);
    if (err) { setError(err.message || 'Sealing failed'); return; }
    refreshSeals();
  };

  const emailDigest = async () => {
    setEmailing(true); setError(''); setEmailMsg('');
    const { data, error: err } = await sendSealDigest({});
    setEmailing(false);
    if (err?.message) { setError(err.message); return; }
    if (data?.error)  { setError(data.error);  return; }
    const queued = Number(data?.enqueued ?? 0);
    const range  = data?.since ? new Date(data.since).toLocaleString() : '';
    setEmailMsg(queued > 0
      ? `Queued ${queued} digest email${queued === 1 ? '' : 's'} to firm leaders covering seals since ${range}. They will be sent on the next outbox flush.`
      : `No new seals since ${range || 'the last digest'} — nothing to email yet.`);
  };

  const tamperedCount = (report || []).filter((r) => r.status === 'tampered').length;
  const cleanCount    = (report || []).filter((r) => r.status === 'ok').length;

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">Tamper-evident sealing</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Every team-chat message carries a SHA-256 hash chained to the previous message in its channel.
            A daily seal anchors each channel's head hash to an append-only ledger so any silent edit becomes
            detectable. Verify any time; capture an extra seal before high-stakes events (audits, depositions).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" onClick={verify} disabled={verifying}>
          {verifying ? 'Verifying…' : 'Verify integrity now'}
        </Button>
        <Button onClick={seal} disabled={sealing}>
          {sealing ? 'Sealing…' : 'Capture seal'}
        </Button>
        <Button variant="ghost" onClick={emailDigest} disabled={emailing}>
          {emailing ? 'Queuing…' : 'Email today’s digest to leaders'}
        </Button>
      </div>

      {emailMsg && (
        <p className="text-xs text-slate-500 dark:text-slate-400">{emailMsg}</p>
      )}

      {report && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className={`px-3 py-2 flex items-center justify-between text-sm font-medium ${
            tamperedCount > 0
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
          }`}>
            <span>
              {tamperedCount > 0
                ? `Tampering detected on ${tamperedCount} channel${tamperedCount === 1 ? '' : 's'}.`
                : `Verified clean: ${cleanCount} channel${cleanCount === 1 ? '' : 's'}.`}
            </span>
            <span className="text-xs opacity-75">
              {report.length} channel{report.length === 1 ? '' : 's'} scanned
            </span>
          </div>
          {report.length > 0 && (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {report.map((r) => {
                const seal = latestSeals[r.channel_id];
                return (
                  <li key={r.channel_id} className="px-3 py-2 flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                      r.status === 'tampered'
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                    }`}>
                      {r.status === 'tampered' ? '!' : '✓'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-800 dark:text-slate-100 truncate">
                        {r.is_private ? r.channel_name : `#${r.channel_name}`}
                        {r.archived ? <span className="ml-1 text-slate-400">(archived)</span> : null}
                      </p>
                      <p className="text-slate-500">
                        {r.message_count} message{r.message_count === 1 ? '' : 's'}
                        {seal && <> · last sealed {new Date(seal.captured_at).toLocaleString()}</>}
                        {!seal && <> · never sealed</>}
                      </p>
                    </div>
                    {r.status === 'tampered' && r.first_break_at && (
                      <span className="text-[11px] text-red-600 dark:text-red-400 flex-shrink-0">
                        first break: {new Date(r.first_break_at).toLocaleString()}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// End-to-end encryption controls. A firm leader sets up a passphrase once;
// all firm staff enter it once per browser tab to read/write encrypted
// channels. The derived key never leaves the browser.
function E2EEncryptionRow() {
  const { userProfile } = useAuth();
  const enc = useEncryption();
  const isLeader = ['Admin','Owner','Partner'].includes(userProfile?.role || '');

  const [pass,    setPass]    = useState('');
  const [pass2,   setPass2]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const [mode,    setMode]    = useState(null); // null | 'setup' | 'unlock' | 'rotate'
  const [localErr, setLocalErr] = useState('');

  const clearForm = () => { setPass(''); setPass2(''); setLocalErr(''); setMode(null); };

  const doSetup = async () => {
    setLocalErr('');
    if (pass !== pass2) { setLocalErr('Passphrases do not match.'); return; }
    setBusy(true);
    const ok = await enc.setup(pass);
    setBusy(false);
    if (ok) { clearForm(); toast.success('End-to-end encryption is set up. Share the passphrase with staff who need encrypted access.'); }
    else    setLocalErr(enc.error || 'Setup failed.');
  };
  const doUnlock = async () => {
    setLocalErr(''); setBusy(true);
    const ok = await enc.unlock(pass);
    setBusy(false);
    if (ok) { clearForm(); toast.success('Encryption unlocked for this session.'); }
    else    setLocalErr(enc.error || 'Wrong passphrase.');
  };
  const doRotate = async () => {
    setLocalErr('');
    if (pass !== pass2) { setLocalErr('New passphrases do not match.'); return; }
    if (!window.confirm('Rotate the firm passphrase? Anyone who only knew the old passphrase will lose access to new messages. Old encrypted messages remain readable with the old passphrase until retention deletes them.')) return;
    setBusy(true);
    const ok = await enc.rotate(pass);
    setBusy(false);
    if (ok) { clearForm(); toast.success('Encryption passphrase rotated. Share the new one with active staff.'); }
    else    setLocalErr(enc.error || 'Rotation failed.');
  };

  const stateBadge = !enc.configured ? <Badge tone="slate">Not configured</Badge>
                   : enc.unlocked    ? <Badge tone="green">Unlocked</Badge>
                                     : <Badge tone="amber">Locked</Badge>;

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">End-to-end encrypted channels</p>
          <p className="text-xs text-slate-500 mt-0.5">
            For channels flagged as encrypted, message bodies are AES-256 encrypted in the browser
            before being stored. The server only sees ciphertext; even a super-admin can't read them.
            Each user enters the firm passphrase once per browser tab to unlock.
            <strong className="ml-1">If the passphrase is forgotten and no one else has it, those messages are unrecoverable.</strong>
          </p>
          {localErr && <p className="text-xs text-red-500 mt-1">{localErr}</p>}
        </div>
        {stateBadge}
      </div>

      {!enc.configured && !isLeader && (
        <p className="text-xs text-slate-500">
          A firm Admin/Owner/Partner needs to set this up first.
        </p>
      )}

      {!enc.configured && isLeader && mode !== 'setup' && (
        <Button onClick={() => setMode('setup')}>Set up encryption</Button>
      )}

      {enc.configured && !enc.unlocked && mode !== 'unlock' && (
        <Button onClick={() => setMode('unlock')}>Unlock encryption</Button>
      )}

      {enc.configured && enc.unlocked && mode !== 'rotate' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={enc.lock}>Lock this session</Button>
          {isLeader && (
            <Button variant="ghost" onClick={() => setMode('rotate')}>Rotate passphrase</Button>
          )}
          <span className="text-xs text-slate-500">Key version {enc.keyVersion}</span>
        </div>
      )}

      {/* Inline forms */}
      {(mode === 'setup' || mode === 'rotate' || mode === 'unlock') && (
        <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
            {mode === 'setup'  && 'Choose a strong firm passphrase. At least 12 characters. Share it with the staff who need encrypted access.'}
            {mode === 'unlock' && 'Enter the firm passphrase to unlock encrypted channels for this browser tab.'}
            {mode === 'rotate' && 'Choose a new firm passphrase. Anyone who only knew the old one will lose access to future messages.'}
          </p>
          <Input
            type="password" autoFocus
            value={pass} onChange={(e) => setPass(e.target.value)}
            placeholder={mode === 'unlock' ? 'Firm passphrase' : 'New firm passphrase'}
          />
          {(mode === 'setup' || mode === 'rotate') && (
            <Input
              type="password"
              value={pass2} onChange={(e) => setPass2(e.target.value)}
              placeholder="Confirm passphrase"
            />
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={clearForm} disabled={busy}>Cancel</Button>
            {mode === 'setup'  && <Button onClick={doSetup}  disabled={busy || !pass || !pass2}>{busy ? 'Setting up…' : 'Set up'}</Button>}
            {mode === 'unlock' && <Button onClick={doUnlock} disabled={busy || !pass}>{busy ? 'Unlocking…' : 'Unlock'}</Button>}
            {mode === 'rotate' && <Button onClick={doRotate} disabled={busy || !pass || !pass2}>{busy ? 'Rotating…' : 'Rotate'}</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

// Data-loss-prevention controls. Each rule has three settings:
//   off   — scanner ignores the pattern
//   warn  — message goes through, event logged for leader review
//   block — message rejected at INSERT time, both server- and client-side
const DLP_RULES = [
  { key: 'rule_credit_card', recommended: 'block' },
  { key: 'rule_ssn_us',      recommended: 'block' },
  { key: 'rule_ghana_card',  recommended: 'warn'  },
  { key: 'rule_api_key',     recommended: 'block' },
  { key: 'rule_jwt',         recommended: 'block' },
  { key: 'rule_iban',        recommended: 'warn'  },
];

const DLP_OPTIONS = [
  { value: 'off',   label: 'Off' },
  { value: 'warn',  label: 'Warn & log' },
  { value: 'block', label: 'Block' },
];

function ruleLabel(key) {
  const short = key.replace(/^rule_/, '');
  return DLP_RULE_LABELS[short] || short;
}

function DlpPolicyRow() {
  const { firmId } = useFirm();
  const [settings, setSettings] = useState(null);
  const [loaded,   setLoaded]   = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [events,   setEvents]   = useState([]);
  const [eventsOpen, setEventsOpen] = useState(false);

  const reload = useCallback(() => {
    if (!firmId) return;
    getFirmDlpSettings(firmId).then(({ data }) => {
      // Mirror the migration defaults so the toggles render even if no row exists yet.
      setSettings(data || {
        enabled: true,
        rule_credit_card: 'block',
        rule_ssn_us: 'block',
        rule_ghana_card: 'warn',
        rule_api_key: 'block',
        rule_jwt: 'block',
        rule_iban: 'warn',
      });
      setLoaded(true);
    });
    listRecentDlpEvents(firmId, 25).then(({ data }) => setEvents(data || []));
  }, [firmId]);

  useEffect(reload, [reload]);

  const update = async (patch) => {
    setSaving(true); setError('');
    const { data, error: err } = await updateFirmDlpSettings(patch);
    setSaving(false);
    if (err) { setError(err.message || 'Could not save'); return; }
    if (data) setSettings((prev) => ({ ...(prev || {}), ...data }));
  };

  if (!loaded) {
    return <div className="border-t border-slate-100 dark:border-slate-800 pt-4 text-xs text-slate-500">Loading DLP settings…</div>;
  }

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">Data-loss prevention (DLP)</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Scan outbound team-chat and portal messages for sensitive patterns at send time.
            Blocked messages never reach the database. Warned messages send through but are logged
            here so leaders can follow up.
          </p>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
        <Badge tone={settings.enabled === false ? 'slate' : 'green'}>
          {settings.enabled === false ? 'Disabled' : 'Active'}
        </Badge>
      </div>

      <label className="inline-flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={settings.enabled !== false}
          onChange={(e) => update({ enabled: e.target.checked })}
          disabled={saving}
        />
        <span>Enable DLP scanning for this firm</span>
      </label>

      {settings.enabled !== false && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 dark:bg-slate-800/40">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Rule</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Recommended</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {DLP_RULES.map((r) => (
                <tr key={r.key}>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{ruleLabel(r.key)}</td>
                  <td className="px-3 py-2">
                    <Select
                      value={settings[r.key] || 'off'}
                      onChange={(e) => update({ [r.key]: e.target.value })}
                      options={DLP_OPTIONS}
                      disabled={saving}
                    />
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs text-slate-500 capitalize">
                    {r.recommended}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setEventsOpen((v) => !v)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {eventsOpen ? 'Hide' : 'Show'} recent DLP events ({events.length})
        </button>
        {eventsOpen && (
          <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {events.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500 text-center">No DLP events recorded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {events.map((e) => {
                  const userLabel = e.user
                    ? [e.user.first_name, e.user.last_name].filter(Boolean).join(' ').trim() || e.user.email
                    : (e.scope === 'portal' ? 'Portal client' : 'Unknown');
                  return (
                    <li key={e.id} className="px-3 py-2 flex items-center gap-3">
                      <span className={`inline-flex w-12 justify-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        e.severity === 'block'
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                          : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                      }`}>
                        {e.severity}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700 dark:text-slate-200">
                          {DLP_RULE_LABELS[e.rule] || e.rule}
                          <span className="font-mono ml-1 text-slate-500">({e.redacted_match})</span>
                        </p>
                        <p className="text-slate-500">
                          {userLabel} · {e.scope} · {formatDate(e.created_at, { includeTime: true })}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Per-firm message retention policy. Soft-deletes team-channel messages
// tied to closed matters after N days. Includes a live preview so leaders
// can see the impact before flipping it on.
const RETENTION_PRESETS = [
  { value: null,    label: 'Off — keep messages indefinitely' },
  { value: 730,     label: '2 years after close (730 days)' },
  { value: 1825,    label: '5 years after close (1825 days)' },
  { value: 2555,    label: '7 years after close (2555 days)' },
  { value: 3650,    label: '10 years after close (3650 days)' },
];

function MessageRetentionRow() {
  const { firm, refresh } = useFirm();
  const current = firm?.message_retention_after_close_days ?? null;
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error,  setError]  = useState('');
  const [saved,  setSaved]  = useState(false);

  useEffect(() => { setValue(current); }, [current]);

  const dirty = value !== current;

  const runPreview = async () => {
    setPreviewing(true);
    const { data, error: err } = await previewMessageRetention(value);
    setPreviewing(false);
    if (err) { setError(err.message || 'Preview failed'); return; }
    setPreview(data || null);
  };

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    const { error: err } = await setFirmMessageRetention(value);
    setSaving(false);
    if (err) { setError(err.message || 'Could not save'); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    refresh();
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">Message retention (closed matters)</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Auto-delete team-channel messages tied to closed matters after the chosen window.
            Only applies to channels linked to a matter. Messages are soft-deleted (a tombstone remains)
            so audit trails stay intact. <strong>Export your archive first</strong> if you need the bodies preserved.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={value === null ? '' : String(value)}
          onChange={(e) => {
            const v = e.target.value;
            setValue(v === '' ? null : Number(v));
          }}
          options={RETENTION_PRESETS.map((p) => ({ value: p.value === null ? '' : String(p.value), label: p.label }))}
        />
        <Button variant="ghost" onClick={runPreview} disabled={previewing}>
          {previewing ? 'Counting…' : 'Preview impact'}
        </Button>
        <Button onClick={save} disabled={!dirty || saving} icon={saved ? CheckCircle : Save}>
          {saved ? 'Saved!' : saving ? 'Saving…' : 'Save policy'}
        </Button>
      </div>

      {preview && (
        <div className="text-xs px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">
          With this policy in place today:
          <strong className="mx-1">{preview.affected_messages}</strong>
          message{preview.affected_messages === 1 ? '' : 's'} across
          <strong className="mx-1">{preview.affected_matters}</strong>
          closed matter{preview.affected_matters === 1 ? '' : 's'} would be deleted on the next sweep
          {preview.oldest_close_date && (
            <> · oldest close date: <strong>{formatDate(preview.oldest_close_date)}</strong></>
          )}
          .
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// OpenTimestamps anchors — Bitcoin-backed external proof of the seal ledger.
// Leaders can trigger an anchor on demand and download the resulting .ots
// proof file for any anchor row.
function BitcoinAnchorsRow() {
  const { firmId } = useFirm();
  const [rows, setRows]       = useState([]);
  const [loaded, setLoaded]   = useState(false);
  const [anchoring, setAnchoring] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeMsg, setUpgradeMsg] = useState('');
  const [error, setError]     = useState('');
  const [open, setOpen]       = useState(false);

  const reload = useCallback(() => {
    if (!firmId) return;
    listSealAnchors(firmId).then(({ data }) => {
      setRows(Array.isArray(data) ? data : []);
      setLoaded(true);
    });
  }, [firmId]);

  useEffect(reload, [reload]);

  const triggerNow = async () => {
    setAnchoring(true); setError('');
    const { data, error: err } = await triggerSealAnchor({ firm_id: firmId });
    setAnchoring(false);
    if (err?.message) { setError(err.message); return; }
    if (data?.error)  { setError(data.error);  return; }
    reload();
  };

  const upgradeNow = async () => {
    setUpgrading(true); setError(''); setUpgradeMsg('');
    const { data, error: err } = await upgradeAnchors({ firm_id: firmId });
    setUpgrading(false);
    if (err?.message) { setError(err.message); return; }
    if (data?.error)  { setError(data.error);  return; }
    const c = data?.confirmed ?? 0;
    const sp = data?.still_pending ?? 0;
    setUpgradeMsg(c > 0
      ? `Confirmed ${c} anchor${c === 1 ? '' : 's'} on Bitcoin · ${sp} still pending.`
      : `No new Bitcoin confirmations yet — ${sp} pending. Check again in an hour.`);
    reload();
  };

  const downloadProof = async (row) => {
    const { data, error: err } = await fetchSealAnchorProof(row.id);
    if (err) { setError(err.message); return; }
    const proof = data?.ots_proof;
    if (!proof) { setError('No proof bytes — anchor may have failed.'); return; }
    // Supabase returns bytea as a hex string '\xHHHH…' over the wire.
    let bytes;
    if (typeof proof === 'string' && proof.startsWith('\\x')) {
      const hex = proof.slice(2);
      bytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    } else if (Array.isArray(proof)) {
      bytes = new Uint8Array(proof);
    } else {
      setError('Unrecognized proof encoding.');
      return;
    }
    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messaging-seal-${row.id}-${new Date(row.created_at).toISOString().slice(0,10)}.ots`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium">Bitcoin anchoring (OpenTimestamps)</p>
          <p className="text-xs text-slate-500 mt-0.5">
            After each seal capture, the head hashes are committed to{' '}
            <a href="https://opentimestamps.org" target="_blank" rel="noreferrer" className="underline">OpenTimestamps</a>{' '}
            which batches them into Bitcoin's blockchain. Once confirmed (typically within a few hours),
            you have a publicly-verifiable proof that the chain existed at that time — no need to trust us
            or the email digest.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Download an <code>.ots</code> proof file and verify it externally with{' '}
            <code>ots verify proof.ots</code> (from the{' '}
            <a href="https://github.com/opentimestamps/opentimestamps-client" target="_blank" rel="noreferrer" className="underline">
              opentimestamps-client
            </a>).
          </p>
          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="ghost" onClick={upgradeNow} disabled={upgrading}>
            {upgrading ? 'Checking…' : 'Check for confirmations'}
          </Button>
          <Button onClick={triggerNow} disabled={anchoring}>
            {anchoring ? 'Anchoring…' : 'Anchor now'}
          </Button>
        </div>
      </div>

      {upgradeMsg && (
        <p className="text-xs text-slate-500 dark:text-slate-400">{upgradeMsg}</p>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
      >
        {open ? 'Hide' : 'Show'} anchor history ({loaded ? rows.length : '…'})
      </button>

      {open && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          {rows.length === 0 ? (
            <p className="px-3 py-4 text-xs text-slate-500 text-center">
              No anchors yet. Click "Anchor now" to create the first one.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Anchored</th>
                  <th className="text-left px-3 py-2 font-medium">Coverage</th>
                  <th className="text-left px-3 py-2 font-medium">Seals</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Proof</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {formatDate(r.created_at, { includeTime: true })}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {formatDate(r.covers_since)} → {formatDate(r.covers_until)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600 dark:text-slate-300">{r.seal_count}</td>
                    <td className="px-3 py-2">
                      <Badge tone={r.status === 'failed' ? 'red' : r.status === 'confirmed' ? 'green' : 'amber'}>
                        {r.status}
                      </Badge>
                      {r.error_text && (
                        <p className="text-[10px] text-red-500 mt-0.5 max-w-xs truncate" title={r.error_text}>
                          {r.error_text}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.status !== 'failed' && (
                        <Button size="sm" variant="ghost" onClick={() => downloadProof(r)}>
                          Download .ots
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// Messaging archive download — calls firm_messaging_export RPC and offers
// the resulting JSON as a file. Admin/Partner gated server-side.
function MessagingExportRow() {
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  const exportNow = async () => {
    setBusy(true);
    setErr('');
    const { data, error } = await firmMessagingExport();
    setBusy(false);
    if (error) { setErr(error.message || 'Export failed'); return; }
    if (!data)  { setErr('Empty export — nothing to download.'); return; }
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `firm-messaging-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t border-slate-100 dark:border-slate-800 pt-4 flex items-center justify-between gap-3">
      <div>
        <p className="font-medium">Messaging archive export</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Download every team channel, message, thread, reaction, and client conversation as a single JSON file.
          Admin/Partner only.
        </p>
        {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
      </div>
      <Button onClick={exportNow} disabled={busy} icon={Upload}>
        {busy ? 'Preparing…' : 'Export'}
      </Button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SettingsPage() {
  return (
    <Can permission="settings.view" fallback={<AccessDenied />}>
      <SettingsPageInner />
    </Can>
  );
}

// ── Case Management Section ──────────────────────────────────────────────────
const RETENTION_OPTIONS = [
  { value: '3',   label: '3 months' },
  { value: '6',   label: '6 months' },
  { value: '12',  label: '12 months (recommended)' },
  { value: '24',  label: '2 years' },
  { value: '36',  label: '3 years' },
  { value: '60',  label: '5 years' },
  { value: '72',  label: '6 years (civil litigation)' },
  { value: '84',  label: '7 years (commercial / criminal)' },
  { value: '120', label: '10 years (family / probate)' },
  { value: '144', label: '12 years (conveyancing / land)' },
];

function CaseManagementSection({ firm, firmId, refresh }) {
  const [retentionMonths, setRetentionMonths] = useState('12');
  const { saving, saved, error, save } = useSaveSection();

  useEffect(() => {
    if (firm) setRetentionMonths(String(firm.matter_retention_months ?? 12));
  }, [firm]);

  const handleSave = () =>
    save(async () => {
      const { error: err } = await updateFirm(firmId, {
        matter_retention_months: Number(retentionMonths),
      });
      if (err) throw err;
      refresh();
    });

  return (
    <Card>
      <CardHeader
        title="Case management"
        subtitle="Configure how closed matters are handled over time."
      />
      <CardBody className="space-y-6">
        <div>
          <Label>Matter retention period</Label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            Closed matters that have exceeded this period will be flagged as
            <span className="font-medium text-amber-600 dark:text-amber-400"> Ready to archive</span> on
            the case files list. No automatic archiving — a partner still makes the final call.
          </p>
          <Select
            value={retentionMonths}
            onChange={(e) => setRetentionMonths(e.target.value)}
            options={RETENTION_OPTIONS}
            className="max-w-xs"
          />
        </div>

        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3 space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Retention guidelines (Ghana)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600 dark:text-slate-400 mt-2">
            <p><span className="font-medium">Civil litigation</span> — 6 years (Limitation Act 1972)</p>
            <p><span className="font-medium">Criminal matters</span> — 7 years</p>
            <p><span className="font-medium">Commercial / corporate</span> — 7 years (Companies Act)</p>
            <p><span className="font-medium">Family / probate</span> — 10–12 years</p>
            <p><span className="font-medium">Conveyancing / land</span> — 12+ years</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button icon={Save} onClick={handleSave} loading={saving}>Save</Button>
          {saved  && <span className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle size={14} /> Saved</span>}
          {error  && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </CardBody>
    </Card>
  );
}

// ── Access log section (Admin/Partner only) ───────────────────────────────────
const ACCESS_LOG_ACTION_META = {
  login:               { icon: LogIn,    label: 'Sign-in',              tone: 'green'  },
  logout:              { icon: LogOut,   label: 'Sign-out',             tone: 'slate'  },
  password_change:     { icon: KeyRound, label: 'Password changed',     tone: 'amber'  },
  password_setup:      { icon: KeyRound, label: 'Password set',         tone: 'amber'  },
  session_resume:      { icon: LogIn,    label: 'Session resumed',      tone: 'slate'  },
  impersonation_start: { icon: Eye,      label: 'Super-admin started viewing firm', tone: 'red' },
  impersonation_end:   { icon: Eye,      label: 'Super-admin stopped viewing firm', tone: 'slate' },
};

function formatAccessLogTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AccessLogSection({ firmId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!firmId) return;
    let cancelled = false;
    listAccessLog(firmId, 200).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) setError(err.message || 'Could not load access log');
      else setRows(data || []);
    });
    return () => { cancelled = true; };
  }, [firmId]);

  return (
    <Card>
      <CardHeader
        title="Access log"
        subtitle="Authentication events for everyone in this firm — sign-ins, sign-outs, password changes, and super-admin access. Visible to Admins and Partners only."
      />
      <CardBody>
        {error && <Alert tone="red" className="mb-4">{error}</Alert>}
        {rows === null && !error && (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
        {rows && rows.length === 0 && (
          <p className="text-sm text-slate-500">No access events recorded yet.</p>
        )}
        {rows && rows.length > 0 && (
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">When</th>
                  <th className="text-left px-4 py-2 font-medium">User</th>
                  <th className="text-left px-4 py-2 font-medium">Event</th>
                  <th className="text-left px-4 py-2 font-medium">IP address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((r) => {
                  const meta = ACCESS_LOG_ACTION_META[r.action] || { icon: Shield, label: r.action, tone: 'slate' };
                  const Icon = meta.icon;
                  const name = r.user ? fullName(r.user) : null;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {formatAccessLogTimestamp(r.created_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-slate-800 dark:text-slate-100">{name || r.user_email || '—'}</p>
                        {name && r.user_email && (
                          <p className="text-xs text-slate-500">{r.user_email}</p>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          <Icon size={13} className="text-slate-500" />
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs font-mono text-slate-500">
                        {r.ip_address || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {rows && rows.length >= 200 && (
          <p className="mt-3 text-xs text-slate-500">Showing the 200 most recent events.</p>
        )}
      </CardBody>
    </Card>
  );
}

function SettingsPageInner() {
  const { firm, firmId, refresh } = useFirm();
  const { userProfile } = useAuth();
  const [{ searchParams }] = useRoute();
  // Allow deep-linking like #/settings?section=team
  const [section, setSection] = useState(() => searchParams?.section || 'firm');
  useEffect(() => {
    if (searchParams?.section && searchParams.section !== section) {
      setSection(searchParams.section);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.section]);

  // Admin / Partner gate for sensitive sections (matches the RLS policy on
  // public.access_log so the UI doesn't show a section that would return zero
  // rows for everyone else anyway).
  const isFirmLeader = userProfile?.role === 'Admin' || userProfile?.role === 'Partner';
  const visibleSections = SECTIONS.filter((s) => !s.leadersOnly || isFirmLeader);

  // If the user lands on a hidden section (e.g. role changed), bounce them.
  useEffect(() => {
    if (!visibleSections.some((s) => s.key === section)) setSection('firm');
  }, [section, visibleSections]);

  // In-page search across section labels + keywords. Empty filter shows all.
  const [filter, setFilter] = useState('');
  const filterTokens = filter.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const matches = (s) => {
    if (filterTokens.length === 0) return true;
    const hay = `${s.label} ${s.description || ''} ${s.keywords || ''}`.toLowerCase();
    return filterTokens.every((t) => hay.includes(t));
  };
  const filteredSections = visibleSections.filter(matches);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Manage your firm profile, team, and billing preferences
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-4">
        {/* Sidebar nav */}
        <div className="md:sticky md:top-20 md:self-start space-y-3">
          {/* In-page search across section labels + keywords */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search settings…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>

          <Card>
            <nav className="p-2 space-y-1">
              {filteredSections.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">No settings match "{filter}"</p>
              ) : filteredSections.map((s) => {
                const Icon = s.icon;
                const active = section === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => setSection(s.key)}
                    className={`w-full flex items-start gap-3 rounded-lg px-3 py-2 text-sm text-left transition ${
                      active
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Icon size={16} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className={active ? 'font-semibold' : 'font-medium'}>{s.label}</p>
                      {s.description && (
                        <p className={`text-[11px] mt-0.5 leading-tight ${active ? 'text-indigo-600/80 dark:text-indigo-300/70' : 'text-slate-500 dark:text-slate-500'}`}>
                          {s.description}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </Card>
        </div>

        {/* Section content */}
        <div>
          {section === 'firm'         && <FirmProfileSection  firm={firm} firmId={firmId} refresh={refresh} />}
          {section === 'team'         && <TeamSection          firm={firm} firmId={firmId} />}
          {section === 'subscription' && <SubscriptionSection  firm={firm} />}
          {section === 'billing'      && <BillingSection        firm={firm} firmId={firmId} refresh={refresh} />}
          {section === 'cases'        && <CaseManagementSection firm={firm} firmId={firmId} refresh={refresh} />}
          {section === 'security'     && <SecuritySection />}
          {section === 'access' && isFirmLeader && <AccessLogSection firmId={firmId} />}
          {section === 'data'   && isFirmLeader && <SampleDataSection />}
        </div>
      </div>
    </div>
  );
}
